import { I18n } from "@assets/strings";
import React from "react";
import styled, { css } from "styled-components";
import { useCurrentProfile } from "@domain/currentProfile/hooks";
import { GroupMemberRole } from "@domain/group/group";
import { GroupMember } from "@domain/group/groupMember";
import { gap } from "@ui/styles/utils";
import { DeleteButton } from "@ui/components/buttons/deleteButton";
import { PrimaryButton } from "@ui/components/buttons/primaryButton";
import { row } from "@ui/components/layout";
import { FollowButton } from "@ui/components/profile/followButton";

interface MemberActionsProps extends React.HTMLAttributes<HTMLDivElement> {
	member: GroupMember;
	deleteMember?: (id: string, role?: GroupMemberRole) => void;
	acceptMember?: (member: GroupMember) => void;
	protectedMember?: string;
	canRemoveItself?: boolean;
	mode?: "stack" | "row";
}
export const MemberActions: React.FC<MemberActionsProps> = ({
	member,
	deleteMember,
	acceptMember,
	protectedMember,
	canRemoveItself,
	mode = "row",
	...htmlProps
}) => {
	const currentProfile = useCurrentProfile();

	const canBeDeleted = (member: GroupMember): boolean => {
		return (canRemoveItself || member.id !== currentProfile?.id) && member.id !== protectedMember;
	};
	const isMemberCanBeDeleted = member.subscriptionStatus && member.role === "MEMBER";
	return (
		<Container {...htmlProps} mode={mode}>
			{!!deleteMember && canBeDeleted(member as GroupMember) ? (
				!!acceptMember && (member as GroupMember).status === "PENDING_HOST_ACCEPTANCE" ? (
					<>
						<AcceptButton
							onClick={(e) => {
								e.preventDefault();
								acceptMember(member as GroupMember);
							}}
						>
							{I18n.t("members.pending.list.accept")}
						</AcceptButton>
						<DeleteButton
							onClick={async (e) => {
								e.preventDefault();
								if (isMemberCanBeDeleted) return;
								deleteMember(member.id, (member as GroupMember).role);
							}}
							text={I18n.t("members.pending.list.reject")}
							disabled={isMemberCanBeDeleted}
						/>
					</>
				) : member.status === "PENDING_GUEST_ACCEPTANCE" ? (
					<DeleteButton
						onClick={async (e) => {
							e.preventDefault();
							if (isMemberCanBeDeleted) return;
							deleteMember(member.id, member.role);
						}}
						text={I18n.t("members.pending.list.cancel_invitation")}
						disabled={isMemberCanBeDeleted}
					/>
				) : (
					<>
						<DeleteButton
							onClick={async (e) => {
								e.preventDefault();
								if (isMemberCanBeDeleted) return;
								deleteMember(member.id, member.role);
							}}
							disabled={isMemberCanBeDeleted}
						/>
						{member.id !== currentProfile?.id && <FollowButton profile={member} />}
					</>
				)
			) : (
				<>{member.id !== currentProfile?.id && <FollowButton profile={member} />}</>
			)}
		</Container>
	);
};

const Container = styled.div.withConfig({
	shouldForwardProp: (prop) => prop !== "mode",
})<{ mode?: "stack" | "row" }>`
	margin-top: 10px;
	${({ mode }) =>
		mode === "row" || !mode
			? css`
					${row(10, "flex-start", "center")};
				`
			: css`
					${gap(20)};
				`}
`;

const AcceptButton = styled(PrimaryButton)`
	padding: 4px 10px;
	font-weight: 600;
	font-size: 0.8125rem;
`;
